import { FC, ReactNode, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import classes from './tutorPenaltyBonus.module.scss'
import { OpenContainer } from "../../../features/openContainer";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { Data } from "./data/Data";
import { IPenaltyBonusItem, IPenaltyBonusSummary, penaltyBonusService, TPenaltyBonus } from "../../../entities/penaltyBonus";
import { AddPenaltyBonus } from "../../../features/addPenaltyBonusTutor";
import { useAppSelector } from "../../../app/store/store";
import { FEATURES, hasFeature } from "../../../entities/my";

interface IProps {
    tutorId: number | null;
    titleIcon?: ReactNode;
    onChange?: () => void;
    collapsible?: boolean;
}

export const TutorPenaltyBonus: FC<IProps> = ({tutorId, titleIcon, onChange, collapsible = false}) => {

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const [items, setItems] = useState<IPenaltyBonusItem[]>([])
    const [summary, setSummary] = useState<IPenaltyBonusSummary | null>(null)
    const [range, setRange] = useState<{from: string, to: string} | null>(null)
    const [trigger, setTrigger] = useState<number>(0)
    const targetUserId = tutorId || my.id
    const canRead = hasFeature(my, FEATURES.TUTORS.PENALTIES_BONUSES_READ, targetUserId)
        || (!tutorId && hasFeature(my, FEATURES.TUTORS.PENALTIES_BONUSES_SELF_READ, targetUserId))
    const canCreate = hasFeature(my, FEATURES.TUTORS.PENALTIES_BONUSES_CREATE, targetUserId)

    useEffect(() => {
        setTrigger(prev => prev + 1)
    }, [isLoading, items, summary])

    if(!canRead && !canCreate){
        return null
    }

    const getData = async (from: string, to: string) => {
        try{
            setIsLoading(true)
            const currentTutorId = tutorId || my.id
            const {summary, items} = await penaltyBonusService.tutorGetPenaltiesBonuses(currentTutorId, from, to)
            setSummary(summary)
            setItems(items)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError && (e.status === 404)){}
            else{
                setGlobalMessage({message: 'Ошибка при получении списка бонусов и штрафов', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = (startDate: Date | null, endDate: Date | null) => {
        if(startDate && endDate){
            const nextRange = {from: getDateUTC(startDate), to: getDateUTC(endDate)}
            setRange(nextRange)
            getData(nextRange.from, nextRange.to)
        }
    }

    const addReq = async (amount: string, comment: string, type: TPenaltyBonus, actualDate: string) => {
        await penaltyBonusService.tutorAddPenaltiesBonuses(tutorId || my.id, amount, comment, type, actualDate)
        onChange?.()
        if(range) await getData(range.from, range.to)
    }

    const deleteReq = async (item: IPenaltyBonusItem) => {
        await penaltyBonusService.tutorDeletePenaltiesBonuses(tutorId || my.id, +item.id)
        onChange?.()
        if(range){
            await getData(range.from, range.to)
            return
        }
        setItems(state => state.filter(currentItem => currentItem.id !== item.id))
    }

    const selfData = canRead && <Data
        setDate={setDate}
        summary={summary}
        items={items}
        isLoading={isLoading}
        onDelete={deleteReq}
        canDelete={canCreate}
    />

    return (
        <section className={classes.container}>
            {
                tutorId
                    ?
                <OpenContainer title="Бонусы и штрафы" icon={titleIcon} trigger={trigger}>
                    <section className={classes.content}>
                        {canCreate && <AddPenaltyBonus
                            request={addReq}
                            bonusLabel="Бонус"
                            penaltyLabel="Штраф"
                        />}
                        {canCreate && canRead && <span className={classes.hr} />}
                        {canRead && <Data
                            setDate={setDate}
                            summary={summary}
                            items={items}
                            isLoading={isLoading}
                            onDelete={deleteReq}
                            canDelete={canCreate}
                        />}
                    </section>
                </OpenContainer>
                    :
                <section className={classes.widget + (collapsible ? ` ${classes.collapsible}` : '')}>
                    {collapsible ? <OpenContainer title="Бонусы и штрафы" allowOverflow trigger={trigger}>
                        <section className={classes.dropdownContent}>{selfData}</section>
                    </OpenContainer> : <>
                        <section className={classes.sign}>Бонусы и штрафы</section>
                        {selfData}
                    </>}
                </section>
            }
        </section>
    )
}
